import PaymentsIcon from "@mui/icons-material/Payments";
import Chip from "@mui/material/Chip";
import {
  BooleanInput,
  Create,
  CreateButton,
  Datagrid,
  Edit,
  FunctionField,
  List,
  NumberInput,
  SelectInput,
  SimpleForm,
  TextField,
  TextInput,
  TopToolbar,
  required,
  usePermissions,
  type CreateProps,
  type EditProps,
  type ListProps,
} from "react-admin";
import CustomEmpty from "@/components/custom-empty";
import { hasPermission } from "@/utils/permission-utils";
import { MADRASA_FEE_SETUP_CREATE } from "../shared/madrasa-permissions";
import { RecordTitle } from "../shared/RecordTitle";

type HeadRecord = { isOneTime?: boolean; isActive?: boolean };

function FeeHeadFields() {
  return (
    <>
      <TextInput
        source="name"
        label="Name"
        validate={required()}
        helperText='For example "Tuition", "Exam fee" or "Admission".'
        fullWidth
      />
      <BooleanInput
        source="isOneTime"
        label="One-time fee (charged once, to new admissions only)"
        helperText="Turn on for fees like admission. You cannot switch this once fees are set for it."
      />
      <NumberInput
        source="sortOrder"
        label="Sort order"
        defaultValue={0}
        min={0}
        helperText="Lower numbers are listed first."
        fullWidth
      />
      <BooleanInput
        source="isActive"
        label="Active"
        defaultValue
        helperText="Turn off for fees you no longer charge instead of deleting them."
      />
    </>
  );
}

const ListActions = () => {
  const { permissions } = usePermissions();
  return (
    <TopToolbar>
      {hasPermission(permissions, MADRASA_FEE_SETUP_CREATE) && <CreateButton />}
    </TopToolbar>
  );
};

const listFilters = [
  <SelectInput
    key="isActive"
    source="isActive"
    label="Status"
    choices={[
      { id: "true", name: "Active" },
      { id: "false", name: "Inactive" },
    ]}
    alwaysOn
  />,
];

export function FeeHeadsList(props: ListProps) {
  return (
    <List
      {...props}
      sort={{ field: "sortOrder", order: "ASC" }}
      perPage={50}
      filters={listFilters}
      actions={<ListActions />}
      empty={
        <CustomEmpty
          title="No fee heads yet"
          subtitle="Add the kinds of fee you charge (tuition, exam, admission), then set amounts per class."
          action={<CreateButton variant="contained" />}
        />
      }
    >
      <Datagrid rowClick="edit" bulkActionButtons={false}>
        <TextField source="name" label="Name" />
        <FunctionField
          label="Charged"
          render={(record: HeadRecord) =>
            record.isOneTime ? "Once (new admissions)" : "Every period"
          }
        />
        <TextField source="sortOrder" label="Sort order" />
        <FunctionField
          label="Status"
          render={(record: HeadRecord) =>
            record.isActive ? (
              <Chip size="small" color="success" label="Active" />
            ) : (
              <Chip size="small" label="Inactive" />
            )
          }
        />
      </Datagrid>
    </List>
  );
}

export function FeeHeadsCreate(props: CreateProps) {
  return (
    <Create {...props} redirect="list" title="Add fee head">
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 520 }}>
        <FeeHeadFields />
      </SimpleForm>
    </Create>
  );
}

export function FeeHeadsEdit(props: EditProps) {
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      title={<RecordTitle prefix="Edit fee head" sources={["name"]} />}
    >
      <SimpleForm warnWhenUnsavedChanges sx={{ maxWidth: 520 }}>
        <FeeHeadFields />
      </SimpleForm>
    </Edit>
  );
}

export default {
  list: FeeHeadsList,
  create: FeeHeadsCreate,
  edit: FeeHeadsEdit,
  icon: PaymentsIcon,
  label: "Fee heads",
  options: { label: "Fee heads" },
  name: "feeHeads",
};
